// Import and re-export shared filter configuration
import type { FilterConfig } from './config';
import { REPORT_FILTER_CONFIGS, SHARED_FILTER_REGISTRY } from './config';

export {
	SHARED_FILTER_REGISTRY as FILTER_REGISTRY,
	REPORT_FILTER_CONFIGS,
} from './config';
export type {
	ColorScheme,
	DisplayFieldConfig,
	FilterConfig,
	FilterOption,
	FilterState,
	FilterType,
	FilterValueExtractor,
	FilterValueFormat,
	QueryMapping,
} from './config';
// Note: FilterRegistry type is not re-exported here to avoid conflict with the FilterRegistry class

/**
 * Tenant-scoped, runtime-registered FilterConfigs. Populated by the frontend
 * after fetching custom-field definitions from the backend so `cf:<fieldKey>`
 * filter ids resolve to real configs through `FilterRegistry.getConfig`.
 *
 * Last-write-wins per filter id — a tenant defining the same custom field key
 * on multiple entity types will see the most-recently-registered config used.
 * The backend filter builder reads `queryMapping.objectTable` from the
 * registered config, so as long as each list page registers its own object
 * types' fields, the right config is in place when filters run.
 */
const dynamicRegistry: Map<string, FilterConfig> = new Map();
const dynamicRegistryListeners: Set<() => void> = new Set();

/**
 * Helper functions for working with filter configurations
 */
export class FilterRegistry {
	/**
	 * Get configuration for a specific filter type. Checks the static
	 * registry first, then the dynamic (runtime-registered) registry
	 * for tenant-scoped custom-field filters.
	 */
	static getConfig(filterType: string): FilterConfig | undefined {
		return (
			SHARED_FILTER_REGISTRY[filterType] ?? dynamicRegistry.get(filterType)
		);
	}

	/**
	 * Register tenant-scoped FilterConfigs at runtime. Used for custom-field
	 * filters synthesized from CustomFieldDefinitionEntity rows on the backend.
	 * Notifies any subscribers so React consumers can re-render.
	 */
	static registerDynamicFilters(configs: Record<string, FilterConfig>): void {
		let changed = false;
		for (const [id, config] of Object.entries(configs)) {
			const existing = dynamicRegistry.get(id);
			// Identity check is cheap and good enough — fetch always returns
			// fresh objects, so different fetches re-trigger notifications.
			if (existing !== config) {
				dynamicRegistry.set(id, config);
				changed = true;
			}
		}
		if (changed) {
			dynamicRegistryListeners.forEach((cb) => {
				cb();
			});
		}
	}

	/**
	 * Subscribe to dynamic registry changes. Returns an unsubscribe function.
	 * Used by the React hook so consumers re-render when custom-field configs
	 * arrive from the backend.
	 */
	static subscribeDynamic(cb: () => void): () => void {
		dynamicRegistryListeners.add(cb);
		return () => {
			dynamicRegistryListeners.delete(cb);
		};
	}

	/**
	 * Snapshot of the dynamic registry contents. Useful for consumers that
	 * want to merge with FILTER_REGISTRY for "all available filters" UIs.
	 */
	static getDynamicFilters(): Record<string, FilterConfig> {
		return Object.fromEntries(dynamicRegistry);
	}

	/**
	 * Get all filter configurations for a specific report
	 */
	static getReportFilters(
		reportType: keyof typeof REPORT_FILTER_CONFIGS,
	): FilterConfig[] {
		const filterTypes = REPORT_FILTER_CONFIGS[reportType];
		return filterTypes
			.map((type) => SHARED_FILTER_REGISTRY[type])
			.filter(Boolean) as FilterConfig[];
	}

	/**
	 * Check if a filter type exists in either the static or dynamic registry.
	 */
	static hasFilter(filterType: string): boolean {
		return (
			filterType in SHARED_FILTER_REGISTRY || dynamicRegistry.has(filterType)
		);
	}

	/**
	 * Get all available filter types — static + dynamic.
	 */
	static getAllFilterTypes(): string[] {
		return [...Object.keys(SHARED_FILTER_REGISTRY), ...dynamicRegistry.keys()];
	}

	/**
	 * Get available filters for a specific list key
	 */
	static getAvailableFilters(listKey: string): FilterConfig[] {
		const availableFilters: FilterConfig[] = [];

		Object.entries(SHARED_FILTER_REGISTRY).forEach(
			([_filterId, filterConfig]) => {
				// Include filter if it has no listKeys restriction or if it includes this listKey
				if (
					filterConfig.listKeys == null ||
					filterConfig.listKeys.length === 0 ||
					filterConfig.listKeys.includes(listKey)
				) {
					availableFilters.push(filterConfig);
				}
			},
		);

		return availableFilters;
	}

	/**
	 * Get default active filter IDs for a list key
	 * Returns filter IDs that apply to the given list key
	 */
	static getDefaultActiveFilters(listKey: string): string[] {
		const defaultFilters: string[] = [];

		Object.entries(SHARED_FILTER_REGISTRY).forEach(
			([filterId, filterConfig]) => {
				// Include filter if it has no listKeys restriction or if it includes this listKey
				if (
					filterConfig.listKeys == null ||
					filterConfig.listKeys.length === 0 ||
					filterConfig.listKeys.includes(listKey)
				) {
					defaultFilters.push(filterId);
				}
			},
		);

		return defaultFilters;
	}

	/**
	 * Validate filters against their definitions for a specific list key
	 */
	static validateFilters(
		filters: Record<string, unknown>,
		listKey: string,
	): Array<{ filterId: string; error: string }> {
		const errors: Array<{ filterId: string; error: string }> = [];

		Object.entries(filters).forEach(([filterType, filterValue]) => {
			const filterConfig = SHARED_FILTER_REGISTRY[filterType];

			// Check if filter exists
			if (filterConfig == null) {
				errors.push({
					filterId: filterType,
					error: `Unknown filter type '${filterType}'`,
				});

				return;
			}

			// Check if filter applies to this list key
			if (
				filterConfig.listKeys != null &&
				filterConfig.listKeys.length > 0 &&
				!filterConfig.listKeys.includes(listKey)
			) {
				errors.push({
					filterId: filterType,
					error: `Filter '${filterType}' does not apply to list key '${listKey}'`,
				});

				return;
			}

			// Check required filters
			if (
				filterConfig.required === true &&
				(filterValue == null ||
					(Array.isArray(filterValue) && filterValue.length === 0))
			) {
				errors.push({
					filterId: filterType,
					error: `${filterConfig.label} is required`,
				});
			}
		});

		return errors;
	}

	/**
	 * Transform filter options from API response to FilterOption format
	 */
	static transformToFilterOptions<T>(
		data: T[],
		config: FilterConfig<T>,
	): Array<{
		id: string | number;
		label: string;
		subtitle?: string;
		detailLines?: Array<string>;
		count?: number;
		data: T;
	}> {
		const options = data
			.map((item) => {
				const idField = config.displayFields.id || ('id' as keyof T);
				const id = item[idField];

				// Validate that the ID exists
				if (id === undefined || id === null) {
					// Warning: Item missing required ID field
					return null;
				}

				let subtitle: string | undefined;

				if (config.displayFields.secondary) {
					const secondaryValue = item[config.displayFields.secondary];

					// Only set subtitle if we have a valid value
					if (
						secondaryValue !== null &&
						secondaryValue !== undefined &&
						secondaryValue !== ''
					) {
						subtitle = String(secondaryValue);

						// Special formatting for probability fields
						if (config.displayFields.secondary === 'm_sm_probability') {
							subtitle = `${secondaryValue}%`;
						}
					}
				}

				const primaryValue = item[config.displayFields.primary];

				// Ensure we have a valid label
				if (
					primaryValue === null ||
					primaryValue === undefined ||
					primaryValue === ''
				) {
					// Warning: Filter item has invalid primary field value
				}

				// Extra small-print lines under the subtitle (e.g. street and
				// city/state/zip on the Child Accounts filter)
				const detailLines = config.displayFields.detailLines
					?.map((format) => format(item))
					.filter((line): line is string => line != null && line !== '');

				return {
					id: id as string | number,
					label: String(primaryValue || ''),
					subtitle,
					detailLines:
						detailLines != null && detailLines.length > 0
							? detailLines
							: undefined,
					count: config.displayFields.count
						? Number(item[config.displayFields.count])
						: undefined,
					data: item,
				};
			})
			.filter((option) => option !== null) as Array<{
			id: string | number;
			label: string;
			subtitle?: string;
			detailLines?: Array<string>;
			count?: number;
			data: T;
		}>;

		if (config.sortOptionsByLabel === true) {
			options.sort((a, b) => a.label.localeCompare(b.label));
		}

		return options;
	}
}
